import React from 'react';

import { LANDING_PAGE_EVENTS } from 'Utilities/analytics';

import { Content } from 'LegacySBCRA/Content/Content';

import { FeesTablePortal } from '../../../../components/transactionFees/FeesTable/FeesTablePortal';
import DialogLoader from '../../../../components/transitions/DialogLoader/DialogLoader';
import { PromoCards } from '../../common/PromoCards/PromoCards';
import { RoundedSection } from '../../common/RoundedSection/RoundedSection';
import Status404 from '../../error/404';
import FastlaneHero from '../../fastlane/home/Hero/Hero';
import type { PageBuilderBlock } from '../@types/landingPages';
import { ContentBlock } from '../Components/ContentBlock/ContentBlock';
import { ExpandableContentWrapper } from '../Components/ExpandableContentWrapper/ExpandableContentWrapper';
import { GuidesSection as Guides } from '../Components/GuidesSection/GuidesSection';
import { RecommendedBy } from '../Components/RecommendedBy/RecommendedBy';
import { SimpleHero } from '../Components/SimpleHero/SimpleHero';
import { Statistics } from '../Components/Statistics/Statistics';
import { Steps } from '../Components/Steps/Steps';
import { Trustpilot } from '../Components/Trustpilot/Trustpilot';
import { useLandingPageData } from '../Hooks/useLandingPageData';

import { landingPageSectionLoadEvent, PAGE_SECTIONS } from './LandingPage.helpers';
import { formatReviews, mapGuidesToSummaryData, mapPromoCardsTiles, mapStepsImages } from './PageBuilder.helpers';

import styles from './LandingPage.scss';

const PageBuilder = () => {
  const { acf, loading } = useLandingPageData();

  if (loading) {
    return (<DialogLoader />);
  }

  if (!acf?.page_builder) {
    return (<Status404 />);
  }

  return (
    <div className={styles.component}>
      {acf?.page_builder?.map((block: PageBuilderBlock, index: number) => {
        const key = `${block.acf_fc_layout}-${index}`;

        switch (block.acf_fc_layout) {
        case 'hero':
          return <FastlaneHero
            key={key}
            analyticsEvents={{
              onLandingPageInputFocus: () => LANDING_PAGE_EVENTS.HERO_VRM_INPUT_FOCUSED(),
            }}
            isHideBreadcrumb={false}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
            subTitle={block.subtitle?.trim() || ''}
            title={block.heading_one?.trim() || ''}
          />;
        case 'hero_simple':
          return <SimpleHero key={key} {...block} />;
        case 'recommended_by':
          return <RoundedSection key={key} withBottomLedge withBottomPadding withTopLedge theme={block.theme}>
            <Content className={styles.pageBuilderSection}>
              <RecommendedBy logos={block.logos} />
            </Content>
          </RoundedSection>;
        case 'trustpilot':
          return <RoundedSection key={key} withBottomLedge withBottomPadding withTopLedge theme={block.theme}>
            <Trustpilot
              title={formatReviews(block.title)}
              wrapperClassName={styles.trustpilotSectionPageBuilder}
            />
          </RoundedSection>;
        case 'faqs':
          return (
            <RoundedSection key={key} withBottomLedge withBottomPadding withTopLedge theme={block.theme}>
              <Content className={styles.pageBuilderFAQSection}>
                <ExpandableContentWrapper
                  {...{ faq: block.faqs, faqTitle: block.title }}
                  onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.FAQ)}
                />
              </Content>
            </RoundedSection>
          );
        case 'steps':
          return (
            <RoundedSection key={key} withBottomLedge withBottomPadding withTopLedge theme={block.theme}>
              <Content className={styles.pageBuilderSection}>
                <Steps
                  steps={mapStepsImages(block.steps)}
                  subtitle={block.subtitle}
                  title={block.title}
                />
              </Content>
            </RoundedSection>
          );
        case 'guides': {
          const customGuides = Array.isArray(block.guides) ? block.guides : [];

          return (
            <Guides
              key={key}
              guides={mapGuidesToSummaryData(customGuides)}
              theme={block.theme}
              title={block.title}
            />
          );
        }
        case 'statistics':
          return <Statistics key={key} stats={block.statistics} theme={block.theme} title={block.title} />;
        case 'content':
          return <ContentBlock key={key} content={block.content} layout={block.layout} theme={block.theme} />;
        case 'promo_cards': {
          const promoCardsTiles = mapPromoCardsTiles(Array.isArray(block.tiles) ? block.tiles : []);

          if (!promoCardsTiles.length) {
            return null;
          }

          return (
            <PromoCards
              key={key}
              onSectionLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.PROMO_CARDS)}
              onTileClick={(label, url) => LANDING_PAGE_EVENTS.PROMO_CARD_CLICK({ label, url })}
              theme={block.theme ?? 'primary-yellow'}
              tiles={promoCardsTiles}
              title={block.title}
            />
          );
        }
        default:
          return null;
        }
      })}
      <FeesTablePortal />
    </div>
  );
};

export default PageBuilder;
